import React, { useEffect, useState } from 'react';
import { Trash } from 'react-feather';
import { AsyncPaginate } from 'react-select-async-paginate';
import { Button, Card, CardBody, Col, Input, Label, Row, UncontrolledTooltip } from 'reactstrap';
import { adminAPIURL } from '../../../../../../../../Config';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { GET_COORDINATES } from '../../../../../../../../../store/actions';

const AddAirport = ({ airport, setAirport, setErrors, errors, isUpdate }) => {
  const dispatch = useDispatch();

  const [sessionToken, setSessionToken] = useState('');
  const [coordinateType, setCoordinateType] = useState('');
  const [terminalIndexForCoords, setTerminalIndexForCoords] = useState(null);

  const { getCoordinate } = useSelector((state: any) => ({
    getCoordinate: state.GetCoordinates.data,
  }));

  useEffect(() => {
    if (getCoordinate !== null) {
      if (coordinateType === 'airport') {
        setAirport(prev => ({
          ...prev,
          latitude: getCoordinate?.data?.latitude,
          longitude: getCoordinate?.data?.longitude,
        }));
      } else if (coordinateType === 'terminal' && terminalIndexForCoords !== null) {
        setAirport(prev => {
          const updatedTerminals = [...prev.terminals];
          updatedTerminals[terminalIndexForCoords] = {
            ...updatedTerminals[terminalIndexForCoords],
            latitude: getCoordinate?.data?.latitude,
            longitude: getCoordinate?.data?.longitude,
          };
          return { ...prev, terminals: updatedTerminals };
        });
      }
    }
  }, [getCoordinate]);

  const handleAirportChange = (field, value) => {
    setAirport(prev => ({ ...prev, [field]: value }));
    if (field === 'name')
      setAirport(prev => ({
        ...prev,
        [field]: value,
        terminals: [{ name: null, latitude: '', longitude: '' }],
      }));
    setErrors(prev => ({ ...prev, [field]: '', latitude: '', longitude: '' }));
  };

  const handleTerminalChange = (index, field, value) => {
    const updatedTerminals = [...airport.terminals];
    updatedTerminals[index][field] = value;
    setAirport(prev => ({ ...prev, terminals: updatedTerminals }));

    // Clear error
    const updatedErrors = [...errors.terminals];
    if (updatedErrors[index]) {
      updatedErrors[index][field] = '';
      updatedErrors[index]['latitude'] = '';
      updatedErrors[index]['longitude'] = '';
      setErrors(prev => ({ ...prev, terminals: updatedErrors }));
    }
  };

  const addTerminal = () => {
    // Validate existing terminals
    const terminalErrors = validateTerminalFields(airport.terminals);
    const hasError = terminalErrors.some(err => err.name || err.latitude || err.longitude);

    if (hasError) {
      setErrors(prev => ({ ...prev, terminals: terminalErrors }));
      return;
    }

    // Add new terminal
    setAirport(prev => ({
      ...prev,
      terminals: [...prev.terminals, { name: '', latitude: '', longitude: '' }],
    }));
    setErrors(prev => ({
      ...prev,
      terminals: [...prev.terminals, { name: '', latitude: '', longitude: '' }],
    }));
  };

  const removeTerminal = index => {
    setAirport(prev => ({
      ...prev,
      terminals: prev.terminals.filter((_, i) => i !== index),
    }));
  };

  const validateTerminalFields = terminals => {
    const terminalErrors = terminals.map(t => ({
      name: !t.name ? 'Required' : '',
      latitude: !t.latitude ? 'Required' : '',
      longitude: !t.longitude ? 'Required' : '',
    }));
    return terminalErrors;
  };

  const generateRandomString = (length = 10) => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      result += characters[randomIndex];
    }
    return result;
  };

  /* ---------------------------- TYPED USER HANDLER ---------------------------- */
  async function loadOptionsForAirport(search) {
    const response = await axios({
      method: 'GET',
      url: `${adminAPIURL}/v2/benefit-type/airport-transfer/find-airports`,
      headers: {
        'Project-Code': `ADMIN_ACCESS`,
      },
      params: {
        search,
        sessionToken,
      },
      withCredentials: true,
    });

    return {
      options: response?.data?.data?.map((data, index) => {
        return {
          value: data.description,
          label: `${data?.description}`,
          completeData: data,
        };
      }),
      hasMore: false,
    };
  }

  async function loadOptionsForTerminal(search) {
    const response = await axios({
      method: 'GET',
      url: `${adminAPIURL}/v2/benefit-type/airport-transfer/find-terminals`,
      headers: {
        'Project-Code': `ADMIN_ACCESS`,
      },
      params: {
        search,
        sessionToken,
        location: `${airport?.latitude},${airport?.longitude}`,
      },
      withCredentials: true,
    });

    return {
      options: response?.data?.data?.map((data, index) => {
        return {
          value: data?.description,
          label: `${data?.description}`,
          completeData: data,
        };
      }),
      hasMore: false,
    };
  }

  /* ---------------------------- ON SELECT USER ---------------------------- */
  const onSelectAirport = event => {
    console.log('event', event);
    handleAirportChange('name', event);
    const match = event?.completeData?.structuredFormatting?.mainText?.match(/\(([^)]+)\)$/);

    handleAirportChange('code', match ? match[1] : '');

    // handleAirportChange('code', event);
    setCoordinateType('airport');
    dispatch({
      type: GET_COORDINATES,
      payload: {
        coordinateType: 'airport',
        urlParams: {
          place_id: event?.completeData?.placeId,
        },
      },
    });
  };

  const onSelectTerminal = (event, index) => {
    handleTerminalChange(index, 'name', event);

    setCoordinateType('terminal');
    setTerminalIndexForCoords(index);

    dispatch({
      type: GET_COORDINATES,
      payload: {
        coordinateType: 'terminal',
        urlParams: {
          place_id: event?.completeData?.placeId,
        },
      },
    });
  };

  const handleMenuOpen = () => {
    const token = generateRandomString(); // generate new UUID when menu opens
    setSessionToken(token);
  };

  return (
    <>
      <Row className="mb-3">
        <Col md="3">
          <Label>Airport Name</Label>
          <AsyncPaginate
            debounceTimeout={500}
            value={airport.name ?? null}
            loadOptions={loadOptionsForAirport}
            onChange={e => onSelectAirport(e)}
            isMulti={false}
            closeMenuOnSelect={true}
            noOptionsMessage={() => 'No results found'}
            cacheUniqs={['code']} // force re-fetch if token changes
            placeholder="Select Airport"
            onMenuOpen={handleMenuOpen}
            isDisabled={isUpdate}
          />
          {errors.name && <div className="text-danger small">{errors.name}</div>}
        </Col>
        <Col md="3">
          <Label>Airport Code</Label>
          <Input value={airport.code} onChange={e => handleAirportChange('code', e.target.value)} placeholder="Enter code" invalid={!!errors.code} />
          {errors.code && <div className="text-danger small">{errors.code}</div>}
        </Col>
        <Col md="3">
          <Label>Latitude</Label>
          <Input
            value={airport.latitude}
            onChange={e => handleAirportChange('latitude', e.target.value)}
            placeholder="Latitude"
            invalid={!!errors.latitude}
            disabled={true}
          />
          {errors.latitude && <div className="text-danger small">{errors.latitude}</div>}
        </Col>
        <Col md="3">
          <Label>Longitude</Label>
          <Input
            value={airport.longitude}
            onChange={e => handleAirportChange('longitude', e.target.value)}
            placeholder="Longitude"
            invalid={!!errors.longitude}
            disabled={true}
          />
          {errors.longitude && <div className="text-danger small">{errors.longitude}</div>}
        </Col>
      </Row>

      <Card className="bg-light p-3">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="fw-semibold m-0">Terminals Configuration</h5>

          {airport.terminals?.length === 1 && (
            <>
              <Button
                id="add-terminal-btn" // ID for tooltip
                className="overlay-disable"
                style={{ width: '200px' }}
                color="primary"
                onClick={() => {
                  setAirport(prev => ({
                    ...prev,
                    terminals: [
                      {
                        name: {
                          value: 'Terminal 1',
                          label: 'Terminal 1',
                          completeData: {
                            description: 'Terminal 1',
                            structuredFormatting: {
                              mainText: 'Terminal 1',
                              secondaryText: 'Terminal 1',
                            },
                          },
                        },
                        latitude: prev?.latitude,
                        longitude: prev?.longitude,
                      },
                    ],
                  }));
                }}
                disabled={airport?.name === null}
              >
                Add Custom Terminal
              </Button>

              {/* Tooltip */}
              <UncontrolledTooltip placement="top" target="add-terminal-btn">
                If you can’t find a terminal, click here
              </UncontrolledTooltip>
            </>
          )}
        </div>

        <hr />

        {airport.terminals.map((terminal, index) => (
          <Row key={index} className="mb-2 align-items-start">
            <Col md="3">
              <Label>Terminal {index + 1}</Label>
              <div>
                <AsyncPaginate
                  debounceTimeout={500}
                  value={terminal.name ?? null}
                  loadOptions={loadOptionsForTerminal}
                  onChange={e => onSelectTerminal(e, index)}
                  isMulti={false}
                  closeMenuOnSelect={true}
                  noOptionsMessage={() => 'No results found'}
                  cacheUniqs={['code']} // force re-fetch if token changes
                  placeholder="Select Terminal"
                  onMenuOpen={handleMenuOpen}
                  isDisabled={airport?.name === null}
                />

                {errors.terminals[index]?.name && <div className="text-danger small">{errors.terminals[index].name}</div>}
              </div>
            </Col>

            <Col md="3">
              <Label>Longitude</Label>
              <div>
                <Input
                  value={terminal.longitude}
                  onChange={e => handleTerminalChange(index, 'longitude', e.target.value)}
                  placeholder="Longitude"
                  invalid={!!errors.terminals[index]?.longitude}
                  disabled={true}
                />
                {errors.terminals[index]?.longitude && <div className="text-danger small">{errors.terminals[index].longitude}</div>}
              </div>
            </Col>

            <Col md="3">
              <Label>Latitude</Label>
              <div>
                <Input
                  value={terminal.latitude}
                  onChange={e => handleTerminalChange(index, 'latitude', e.target.value)}
                  placeholder="Latitude"
                  invalid={!!errors.terminals[index]?.latitude}
                  disabled={true}
                />
                {errors.terminals[index]?.latitude && <div className="text-danger small">{errors.terminals[index].latitude}</div>}
              </div>
            </Col>

            <Col md="3" className="mt-4">
              {airport.terminals.length > 1 && (
                <>
                  <div
                    className="cursor-pointer"
                    style={{ background: 'rgba(244, 105, 105, 1)', width: '28px', height: '29px', display: 'flex', borderRadius: '5px' }}
                    onClick={() => removeTerminal(index)}
                  >
                    {' '}
                    <i className=" ri-delete-bin-5-line m-auto fs-15" style={{ color: 'white' }}></i>
                  </div>
                </>
              )}
            </Col>
          </Row>
        ))}
        <Row className="m-0 p-0">
          <Col md={{ size: 3, offset: 8 }}>
            <Button color="link" className="ps-0" onClick={addTerminal} style={{ color: 'rgba(64, 81, 137, 1)' }}>
              + Add New
            </Button>
          </Col>
        </Row>
      </Card>
    </>
  );
};

export default AddAirport;
